import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  addDocument,
  addPage,
  addTagToDocument,
  getDocumentPages,
} from "../utils/Api";
import { useAuth } from "../utils/AuthContext";
import Config from "../Config";

type UploadStatus =
  | "pending"
  | "uploading"
  | "processing"
  | "complete"
  | "error";

type InsertMode = "start" | "after" | "end";

export interface UploadItem {
  id: string;
  documentId: string | null;
  documentName: string;
  mode: "create" | "add";
  files: File[];
  status: UploadStatus;
  progress: number;
  currentStep: string;
  error: string | null;
  createdAt: number;
  selectedTags: string[];
  selectedOrganisation: string;
  existingDocumentId?: string | undefined;
  insertMode?: InsertMode | undefined;
  insertPageNum?: number | undefined;
}

interface StartUploadConfig {
  documentName: string;
  files: File[];
  mode: "create" | "add";
  selectedTags?: string[];
  selectedOrganisation?: string;
  existingDocumentId?: string | undefined;
  insertMode?: InsertMode | undefined;
  insertPageNum?: number | undefined;
}

interface UploadContextType {
  uploads: UploadItem[];
  isWidgetVisible: boolean;
  isWidgetMinimized: boolean;
  startUpload: (config: StartUploadConfig) => string;
  dismissUpload: (uploadId: string) => void;
  dismissAllCompleted: () => void;
  toggleWidgetMinimized: () => void;
  hideWidget: () => void;
  showWidget: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

interface UploadProviderProps {
  children: ReactNode;
}

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const { backendToken } = useAuth();
  const wsConnections = useRef<Map<string, WebSocket>>(new Map());
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const cancelledUploads = useRef<Set<string>>(new Set());
  const accumulatedStatuses = useRef<Map<string, Record<string, string>>>(new Map());

  const updateUpload = useCallback(
    (uploadId: string, updates: Partial<UploadItem>) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === uploadId ? { ...u, ...updates } : u)),
      );
    },
    [],
  );

  const calculateProgress = useCallback(
    (
      pageStatuses: Record<string, string>,
      existingCompletePages: number,
    ): { progress: number; isComplete: boolean } => {
      const statusValues: Record<string, number> = {
        "PageStatus.PENDING": 0,
        "PageStatus.EXTRACTING": 1,
        "PageStatus.STRUCTURING": 2,
        "PageStatus.COMPLETE": 3,
        "PageStatus.FAILED": 3,
        PENDING: 0,
        EXTRACTING: 1,
        STRUCTURING: 2,
        COMPLETE: 3,
        FAILED: 3,
        pending: 0,
        extracting: 1,
        structuring: 2,
        complete: 3,
        failed: 3,
      };

      const statusArray = Object.values(pageStatuses);
      if (statusArray.length === 0) return { progress: 0, isComplete: false };

      const totalPages = statusArray.length - existingCompletePages;
      if (totalPages <= 0) return { progress: 0, isComplete: false };

      const maxTotalProgress = totalPages * 3;
      const currentTotalProgress =
        statusArray.reduce((acc, status) => {
          return acc + (statusValues[status] ?? 0);
        }, 0) -
        3 * existingCompletePages;

      const progress = Math.round(
        (currentTotalProgress / maxTotalProgress) * 100,
      );
      const isComplete = currentTotalProgress >= maxTotalProgress;

      return { progress: Math.min(progress, 100), isComplete };
    },
    [],
  );

  const startWebSocketMonitoring = useCallback(
    (uploadId: string, documentId: string, existingCompletePages: number) => {
      if (!backendToken) return;

      const scheme = Config.DISABLE_SSL ? "ws" : "wss";
      const socketUrl = `${scheme}://${Config.HOSTNAME}/api/document/${documentId}?token=${backendToken}`;

      const ws = new WebSocket(socketUrl);
      wsConnections.current.set(uploadId, ws);

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.status) {
            // Accumulate page statuses so progress is calculated from all pages
            const current = accumulatedStatuses.current.get(uploadId) || {};
            const merged = { ...current, ...message.status };
            accumulatedStatuses.current.set(uploadId, merged);

            const { progress, isComplete } = calculateProgress(
              merged,
              existingCompletePages,
            );

            if (isComplete) {
              updateUpload(uploadId, {
                status: "complete",
                progress: 100,
                currentStep: "Complete",
              });
              accumulatedStatuses.current.delete(uploadId);
              ws.close();
              wsConnections.current.delete(uploadId);
            } else {
              updateUpload(uploadId, {
                progress,
                currentStep: `Processing... ${progress}%`,
              });
            }
          }
        } catch (error) {
          console.error("WebSocket message parse error:", error);
        }
      };

      ws.onerror = () => {
        console.warn("WebSocket error, falling back to polling");
        ws.close();
        wsConnections.current.delete(uploadId);
        startPolling(uploadId, documentId, existingCompletePages);
      };

      ws.onclose = () => {
        const wasTracked = wsConnections.current.has(uploadId);
        wsConnections.current.delete(uploadId);
        if (wasTracked) {
          // Check upload status and fall back to polling outside the state setter
          // to avoid triggering side-effects inside a React state updater function.
          setUploads((prev) => {
            const upload = prev.find((u) => u.id === uploadId);
            if (upload && upload.status === "processing") {
              setTimeout(
                () => startPolling(uploadId, documentId, existingCompletePages),
                0,
              );
            }
            return prev;
          });
        }
      };
    },
    [backendToken, calculateProgress, updateUpload],
  );

  const startPolling = useCallback(
    (uploadId: string, documentId: string, existingCompletePages: number) => {
      const existingInterval = pollingIntervals.current.get(uploadId);
      if (existingInterval) {
        clearInterval(existingInterval);
      }

      const poll = async () => {
        try {
          const { data: pagesArray } = await getDocumentPages(documentId);
          const pages: Record<string, string> = {};
          for (const page of pagesArray) {
            pages[page.id] = page.status;
          }
          const { progress, isComplete } = calculateProgress(
            pages,
            existingCompletePages,
          );

          if (isComplete) {
            updateUpload(uploadId, {
              status: "complete",
              progress: 100,
              currentStep: "Complete",
            });
            const interval = pollingIntervals.current.get(uploadId);
            if (interval) {
              clearInterval(interval);
              pollingIntervals.current.delete(uploadId);
            }
          } else {
            updateUpload(uploadId, {
              progress,
              currentStep: `Processing... ${progress}%`,
            });
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      };

      poll();
      const interval = setInterval(poll, 1000);
      pollingIntervals.current.set(uploadId, interval);
    },
    [calculateProgress, updateUpload],
  );

  const processUpload = useCallback(
    async (upload: UploadItem) => {
      const {
        id: uploadId,
        files,
        mode,
        selectedTags,
        selectedOrganisation,
        existingDocumentId,
        insertMode,
        insertPageNum,
      } = upload;

      try {
        let documentId: string;
        let existingCompletePages = 0;

        if (cancelledUploads.current.has(uploadId)) return;

        if (mode === "create") {
          updateUpload(uploadId, {
            status: "uploading",
            currentStep: "Creating document...",
          });
          const response = await addDocument(
            upload.documentName,
            selectedOrganisation || undefined,
          );
          if (cancelledUploads.current.has(uploadId)) return;
          documentId = response.data.id;
          updateUpload(uploadId, { documentId });
        } else {
          if (!existingDocumentId) {
            throw new Error("Document ID required for adding pages");
          }
          documentId = existingDocumentId;
          updateUpload(uploadId, { documentId, status: "uploading" });

          const { data: existingPagesArray } =
            await getDocumentPages(documentId);
          if (cancelledUploads.current.has(uploadId)) return;
          existingCompletePages = existingPagesArray.length;
        }

        if (cancelledUploads.current.has(uploadId)) return;
        updateUpload(uploadId, {
          currentStep: `Uploading ${files.length} file${files.length > 1 ? "s" : ""}...`,
        });
        const staggerDelay = (ms: number) =>
          new Promise<void>((resolve) => setTimeout(resolve, ms));
        const STAGGER_MS = 150;
        await Promise.all(
          files.map((file, i) => {
            if (!file) return Promise.resolve();
            const currentInsertPageNum =
              insertMode === "after" && insertPageNum !== undefined
                ? insertPageNum + i
                : insertPageNum;
            return staggerDelay(i * STAGGER_MS).then(() =>
              addPage(documentId, file, null, insertMode, currentInsertPageNum),
            );
          }),
        );

        if (cancelledUploads.current.has(uploadId)) return;

        if (mode === "create" && selectedTags.length > 0) {
          updateUpload(uploadId, { currentStep: "Adding tags..." });
          for (const tagId of selectedTags) {
            if (cancelledUploads.current.has(uploadId)) return;
            try {
              await addTagToDocument(tagId, documentId);
            } catch (error) {
              console.error("Failed to add tag:", error);
            }
          }
        }

        if (cancelledUploads.current.has(uploadId)) return;

        updateUpload(uploadId, {
          status: "processing",
          currentStep: "Processing...",
        });

        if (mode === "add") {
          startPolling(uploadId, documentId, existingCompletePages);
        } else {
          startWebSocketMonitoring(uploadId, documentId, existingCompletePages);
        }
      } catch (error: any) {
        if (!cancelledUploads.current.has(uploadId)) {
          console.error("Upload error:", error);
          updateUpload(uploadId, {
            status: "error",
            error: error.message || "Upload failed",
            currentStep: "Failed",
          });
        }
      }
    },
    [updateUpload, startWebSocketMonitoring, startPolling],
  );

  useEffect(() => {
    const pendingUpload = uploads.find((u) => u.status === "pending");
    if (pendingUpload) {
      processUpload(pendingUpload);
    }
  }, [uploads, processUpload]);

  useEffect(() => {
    return () => {
      wsConnections.current.forEach((ws) => ws.close());
      pollingIntervals.current.forEach((interval) => clearInterval(interval));
      cancelledUploads.current.clear();
      accumulatedStatuses.current.clear();
    };
  }, []);

  const startUpload = useCallback((config: StartUploadConfig): string => {
    const uploadId = crypto.randomUUID();
    const newUpload: UploadItem = {
      id: uploadId,
      documentId: null,
      documentName: config.documentName,
      mode: config.mode,
      files: config.files,
      status: "pending",
      progress: 0,
      currentStep: "Queued",
      error: null,
      createdAt: Date.now(),
      selectedTags: config.selectedTags || [],
      selectedOrganisation: config.selectedOrganisation || "",
      existingDocumentId: config.existingDocumentId,
      insertMode: config.insertMode,
      insertPageNum: config.insertPageNum,
    };

    setUploads((prev) => [...prev, newUpload]);
    setIsWidgetVisible(true);
    setIsWidgetMinimized(false);

    return uploadId;
  }, []);

  const dismissUpload = useCallback((uploadId: string) => {
    cancelledUploads.current.add(uploadId);

    const ws = wsConnections.current.get(uploadId);
    if (ws) {
      ws.close();
      wsConnections.current.delete(uploadId);
    }
    const interval = pollingIntervals.current.get(uploadId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(uploadId);
    }

    accumulatedStatuses.current.delete(uploadId);

    setUploads((prev) => prev.filter((u) => u.id !== uploadId));

    // Clean up cancelled flag after a delay to ensure any pending ops complete
    setTimeout(() => {
      cancelledUploads.current.delete(uploadId);
    }, 1000);
  }, []);

  const dismissAllCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((u) => u.status !== "complete" && u.status !== "error"),
    );
  }, []);

  const toggleWidgetMinimized = useCallback(() => {
    setIsWidgetMinimized((prev) => !prev);
  }, []);

  const hideWidget = useCallback(() => {
    setIsWidgetVisible(false);
  }, []);

  const showWidget = useCallback(() => {
    setIsWidgetVisible(true);
  }, []);

  useEffect(() => {
    if (uploads.length === 0) {
      setIsWidgetVisible(false);
    }
  }, [uploads.length]);

  const value: UploadContextType = {
    uploads,
    isWidgetVisible,
    isWidgetMinimized,
    startUpload,
    dismissUpload,
    dismissAllCompleted,
    toggleWidgetMinimized,
    hideWidget,
    showWidget,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
